import { useCallback, useEffect, useRef, useState } from 'react';

import { logService } from '../services';

/**
 * Hook for testing microphone input by measuring volume levels
 */
const useMicrophoneTest = () => {
  const [isTesting, setIsTesting] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const stopTesting = useCallback(() => {
    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setVolumeLevel(0);
    setIsTesting(false);
  }, []);

  const testMicrophone = useCallback(async(deviceId: string) => {
    if (isTesting) {
      logService.addLog('Microphone test already in progress');
      return;
    }

    try {
      setIsTesting(true);
      logService.addLog(`Testing microphone: ${deviceId}`);

      // Request microphone access with specific device
      const constraints: MediaStreamConstraints = {
        audio: {
          deviceId: deviceId === 'default' ? undefined : { exact: deviceId },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Create audio context and analyser
      audioContextRef.current = new AudioContext();
      const audioContext = audioContextRef.current;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.1; // Lower = more responsive, faster fall
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Function to continuously measure volume
      const measureVolume = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average volume with more weight on louder frequencies
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;

        // Normalize to 0-100 range with amplification for better visibility
        const normalizedVolume = Math.min(100, Math.round((average / 255) * 300));
        setVolumeLevel(normalizedVolume);

        animationFrameRef.current = requestAnimationFrame(measureVolume);
      };

      measureVolume();
      logService.addLog('Microphone test started');
    } catch (error) {
      const message = `Error testing microphone: ${error}`;
      logService.addLog(message);
      console.error('Microphone test error:', error);
      stopTesting();
    }
  }, [isTesting, stopTesting]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTesting();
    };
  }, [stopTesting]);

  return {
    testMicrophone,
    stopTesting,
    isTesting,
    volumeLevel,
  };
};

export default useMicrophoneTest;
